export class UnauthorizedError extends Error {
  constructor() {
    super("Sua sessão expirou. Entre novamente.");
    this.name = "UnauthorizedError";
  }
}

/**
 * The resource does not exist, or belongs to someone else — the API answers 404
 * to both on purpose, so ids can't be probed for existence.
 */
export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotFoundError";
  }
}

/**
 * This part of the caderno is not on the student's plan.
 *
 * The trilha gate: a free account may open the first parada and nothing else, so
 * every endpoint that serves a tópico's content — the manual, a quiz, an exame, a
 * simulado — answers 402 for the rest (lib/lume/TrilhaGate.php).
 *
 * ONE CLASS FOR ALL OF THEM, raised in apiFetch rather than per caller: the gate
 * covers seven endpoints, and a screen that has to recognise "locked" from a
 * status code is a screen that will eventually forget to. Callers branch on the
 * type and show the TM PLUS surface.
 *
 * 402 rather than 403 on purpose. The caderno IS theirs — 403 would say it is
 * not, which is both wrong and the wrong screen.
 */
export class TrilhaLockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TrilhaLockedError";
  }
}

/**
 * The request never reached the API: offline, DNS, a dropped connection, a
 * request the browser refused.
 *
 * Worth its own type because it is the one failure the student can actually do
 * something about, and because `fetch` rejects with a bare TypeError whose
 * message ("Failed to fetch", "Load failed" — it differs per browser) is
 * English, technical, and was never written to be read by anyone.
 */
export class NetworkError extends Error {
  constructor() {
    super("Não conseguimos falar com o servidor. Verifique sua conexão.");
    this.name = "NetworkError";
  }
}

/**
 * The API answered, but not with the JSON envelope — a 500 that rendered a PHP
 * error page, a Varnish/Cloudflare error, a redirect to a login page.
 *
 * Without this, `response.json()` throws a SyntaxError about an unexpected "<"
 * and the student is shown a parser's complaint about markup they never saw.
 */
export class ServerError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(
      "O servidor não respondeu como esperávamos. Tente de novo daqui a pouco."
    );
    this.name = "ServerError";
    this.status = status;
  }
}

/**
 * A failure the API described itself, in the `{data: "…"}` envelope.
 *
 * The status rides along because it is what decides whether trying again could
 * possibly help. A 422 is a verdict on the request — "Este tema ainda não tem
 * exercícios." will say exactly the same thing the second time — so both the
 * retry button and React Query's automatic retries have to be able to tell it
 * apart from a 500, which is often just bad luck.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }

  /** 5xx is worth another go; 429 is explicitly "later". A 4xx is a verdict. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

// Shared fetch wrapper: every Lume endpoint goes through here so a 401
// (expired/invalid session) surfaces as a typed error that the global
// QueryClient handler can turn into a redirect to /login.
export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(url, init);
  } catch {
    // fetch only rejects when the request never completed — every HTTP status,
    // including 500, resolves. So this branch is always a transport failure.
    throw new NetworkError();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let json: { data?: unknown };

  try {
    json = await response.json();
  } catch {
    throw new ServerError(response.status);
  }

  const message = typeof json?.data === "string" ? json.data : null;

  if (response.status === 404) {
    throw new NotFoundError(message ?? "Não encontrado.");
  }

  // Before the generic !ok branch: this one has a screen of its own, and falling
  // through would show it as an error message the student cannot act on.
  if (response.status === 402) {
    throw new TrilhaLockedError(
      message ?? "Esta parte do seu roteiro é do Toda Matéria+."
    );
  }

  if (!response.ok) {
    // A 4xx/5xx that DID carry the envelope: the API writes those messages for
    // the student (422 especially), so they are shown verbatim. Only a status
    // with no message of its own falls back to the generic server wording.
    if (message === null) {
      throw new ServerError(response.status);
    }

    throw new ApiError(response.status, message);
  }

  return json.data as T;
}
